import { Mobile } from '@bimser/common';
import {
	BCComboBoxRef,
	BCCombobox,
	BCDivider,
	BCLoading,
	BCMobilePicker,
	IComboboxProps,
	IComboboxSelectedChangeEventArgs,
	findMessage
} from '@bimser/components';
import { FormItemTypes, IDictionary, IItem } from '@bimser/core';
import IconRefresh from '@bimser/icons/16/refresh';
import GetIconByName from '@bimser/icons/lib/getIcon16ByName';
import { Map } from 'immutable';
import React, { JSX } from 'react';
import { ICSSProperties, IPropertyField, SizeType } from '../../../../entities/common';
import ComboBoxBase from '../../base/ComboBoxBase';
import Styles from './assets/styles.scss';
import { IComboBoxProperties } from './entities/IComboBoxProperties';

const comboBoxStyles: ICSSProperties = { width: '100%' };
const dropdownRenderDivider: ICSSProperties = { margin: '0' };

export default class ComboBox extends ComboBoxBase {
	showSearch: boolean;
	sizeType: SizeType;
	inputRef = React.createRef<BCComboBoxRef>();
	hideForceRefreshButton: boolean;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.ComboBox;

		this.hideForceRefreshButton = false;
		this.showSearch = false;
		this._hideCommonLoadingOverlay = true;

		this.triggerValueChanged = this.triggerValueChanged.bind(this);
		this.focus = this.focus.bind(this);
		this.getPopupContainer = this.getPopupContainer.bind(this);
		this.__getItems = this.__getItems.bind(this);
		this.__dropdownRender = this.__dropdownRender.bind(this);
		this.__getSizeType = this.__getSizeType.bind(this);
		this.__getControlByPlatform = this.__getControlByPlatform.bind(this);
		this.__renderDropdownFooter = this.__renderDropdownFooter.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			const changedItem =
				value && this.items.find(i => i?.key?.toString() == value?.toString());
			this.triggerValueChanged({ data: changedItem.value, senderArgs: {} });
		});

		this.subscribe('trigger:OnDynamicSelectSearch', (payload: string) => {
			try {
				const searchValue = JSON.parse(payload);
				this.__onSearch(searchValue.value);
			} catch (error) {
				console.error(error);
			}
		});
	}

	__loadProperties(data: IComboBoxProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.showSearch = data.showSearch;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.hideForceRefreshButton = data.hideForceRefreshButton;
	}

	__getDefaultValue() {
		if (
			(this.__form.documentId || this.__form.documentId == 0) &&
			this.dataSource?.defaultValue
		) {
			return this.dataSource.defaultValue;
		}
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields(properties);
		return [
			...fields,
			{
				name: 'showSearch',
				type: 'boolean'
			},
			{
				name: 'hideForceRefreshButton',
				type: 'boolean'
			}
		];
	}

	__getComponentData(): IComboboxProps & IDictionary<any> {
		return {
			...super.__getComponentData(),
			showSearch: this.showSearch,
			isStaticSearch: this.dataCount < 100,
			hideForceRefreshButton: this.hideForceRefreshButton,
			options: this.__getItems(),
			disabled: this.loading || this.__isDisabled(),
			className: this.style.backgroundColor ? Styles.ComboboxBackgroundClass : '',
			size: this.sizeType,
			style: { ...comboBoxStyles, ...({ '--bgColor': this.style.backgroundColor } as any) }
		};
	}

	protected triggerValueChanged(args: IComboboxSelectedChangeEventArgs) {
		this.__onSearch('');
		const changedItem = args.data && this.items.find(i => i.value == args.data);
		this.__proxyChanging.selectedItemValues = [args.data];
		this.__proxyChanging.items = this.items.map(item => ({
			...item,
			text: item.text,
			selected: changedItem ? item.value === changedItem.value : false
		}));
	}

	__getItems(): IItem[] {
		return this.items.length
			? this.__sortItems(
					Object.values(this.__itemsCollection).map(i => ({
						key: i.key,
						value: i.value,
						selected: this.selectedItemValues?.includes(i.value),
						text: i.text.getText(),
						icon: GetIconByName(i.icon)?.info || null,
						externalData: { iconClassName: Styles.ComboboxIcon }
					}))
			  )
			: [];
	}

	__renderDropdownFooter() {
		const footer = [
			<BCDivider
				key="divider"
				styles={dropdownRenderDivider}
			/>
		];

		if (this.items?.length >= 100 && !this.__searchValue) {
			footer.push(
				<div
					key="search-more"
					className={Styles.searchMore}
				>
					{findMessage.get('102837')}
				</div>
			);
		}

		if (!this.hideForceRefreshButton && !this.dataSource.runAtServer) {
			footer.push(
				<div
					key="force-refresh"
					className={Styles.dropdownRender}
				>
					<a onClick={() => this.reload(true)}>
						<IconRefresh />
						{findMessage.get('102660')}
					</a>
				</div>
			);
		}

		return footer.length ? footer : <></>;
	}

	__dropdownRender(menu: React.ReactElement) {
		return (
			<div>
				{menu}
				{this.__renderDropdownFooter()}
			</div>
		);
	}

	focus() {
		this.inputRef.current?.focus();
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	private getPopupContainer(triggerNode: Element) {
		const scrollableArea = triggerNode.closest('#bcScrollableFormArea');

		if (scrollableArea) {
			return scrollableArea.childNodes[1].firstChild as HTMLElement;
		}

		return triggerNode.parentNode as HTMLElement;
	}

	__getControlByPlatform() {
		if (
			this.__form.__fromMobile &&
			this.__form.__mobileFormView === Mobile.MobileFormView.WebView
		) {
			return (
				<BCMobilePicker
					onSelectedChange={this.triggerValueChanged}
					buttonText={this.value?.toString() || ''}
					showSearch={this.dataSource.name ? true : this.showSearch}
					options={this.__getItems()}
					value={this.value}
					onSearch={this.dataSource.name && this.__onSearch}
					style={{
						...comboBoxStyles,
						...({ backgroundColor: this.style.backgroundColor } as any)
					}}
					disabled={this.__isDisabled()}
					readonly={this.__isReadOnly()}
					allowClear={this.allowClear}
					size={this.sizeType}
					placeholder={this.placeholder.getText()}
					getPopupContainer={this.__form.__getShadowRootContainer}
					loading={this.loading}
				/>
			);
		}

		return (
			<BCCombobox
				tabIndex={this.tabIndex}
				showSearch={this.dataSource.name ? true : this.showSearch}
				allowClear={this.allowClear}
				options={this.__getItems()}
				value={this.value}
				disabled={this.__isDisabled()}
				readonly={this.__isReadOnly()}
				onSelectedChange={this.triggerValueChanged}
				placeholder={this.placeholder.getText()}
				className={this.style.backgroundColor ? Styles.ComboboxBackgroundClass : ''}
				style={{
					...comboBoxStyles,
					...({ backgroundColor: this.style.backgroundColor } as any)
				}}
				size={this.__getSizeType()}
				notFoundContent={this.loading ? <BCLoading size="small" /> : null}
				onSearch={this.showSearch || this.dataSource.name ? this.__onSearch : undefined}
				dropdownRender={this.dataSource.name && this.__dropdownRender}
				dropdownClassName={Styles.comboboxDropdownStyles}
				fromMobile={this.__form.__fromMobile}
				ref={this.inputRef}
				getPopupContainer={this.getPopupContainer}
			/>
		);
	}

	__render(children?: JSX.Element) {
		return super.__render(this.__getControlByPlatform());
	}
}
